/**
 * Shadow rules (W1-03 core-model-ops; DESIGN.md §6.12, contract `SHADOW_RULES` in core/ops/types.ts).
 *
 * Editing a modelled field drops the provenance fields that encode the same meaning, so raw imported values can
 * never contradict the model. A rule fires only when the patch really CHANGES its field (setting `t` to the same
 * beat keeps the raw triple, which still matches). Cleared paths are one level deep:
 *  - fields of `src` (optional provenance) are removed, and `src` itself is removed once it is empty;
 *  - other nested or top-level fields (`rpe.isCoverRaw`, `rpeVersionRaw`) are nullable and are set to null.
 * A provenance value the patch sets explicitly (different from the current one) wins over the clear.
 */

import { fieldOf, valueEquals } from './equal';
import { SHADOW_RULES, type RecordPatch, type ShadowRule } from './types';
import type { PackageSettings, TableName, TableRecords } from '../model/types';

export { SHADOW_RULES };

const RULES_BY_TBL = new Map<TableName | 'pkg', ShadowRule[]>();
for (const rule of SHADOW_RULES) {
  const list = RULES_BY_TBL.get(rule.tbl);
  if (list === undefined) RULES_BY_TBL.set(rule.tbl, [rule]);
  else list.push(rule);
}

/** Containers whose cleared fields are removed (optional provenance) instead of set to null. */
const OPTIONAL_CONTAINERS: ReadonlySet<string> = new Set(['src']);

type Rec = Readonly<Record<string, unknown>>;

/**
 * Applies the rules of `tbl` to `patch` for a record currently equal to `current`; returns the patch itself when
 * no rule fires.
 */
export function applyShadowRules(tbl: TableName | 'pkg', current: object, patch: object): Record<string, unknown> {
  const rules = RULES_BY_TBL.get(tbl);
  const p = patch as Rec;
  if (rules === undefined) return p;
  let out: Record<string, unknown> | null = null;
  for (const rule of rules) {
    if (!Object.prototype.hasOwnProperty.call(p, rule.field)) continue;
    if (valueEquals(fieldOf(current, rule.field), p[rule.field])) continue;
    for (const path of rule.clears) {
      out ??= { ...p };
      clearPath(out, current as Rec, p, path);
    }
  }
  return out ?? p;
}

/** Clears `path` in `out` (a patch being built), based on the current record and the original patch. */
function clearPath(out: Record<string, unknown>, current: Rec, patch: Rec, path: string): void {
  const dot = path.indexOf('.');
  if (dot < 0) {
    // Top-level nullable provenance field (e.g. pkg.rpeVersionRaw).
    if (Object.prototype.hasOwnProperty.call(patch, path) && !valueEquals(patch[path], current[path])) return;
    if (current[path] === null || current[path] === undefined) return;
    out[path] = null;
    return;
  }
  const container = path.slice(0, dot);
  const field = path.slice(dot + 1);
  const explicit = Object.prototype.hasOwnProperty.call(patch, container) ? patch[container] : undefined;
  const curObj = current[container];
  // The container value the patch would produce, before this clear.
  const base = (Object.prototype.hasOwnProperty.call(out, container) ? out[container] : curObj) as
    Rec | null | undefined;
  if (base === null || base === undefined || typeof base !== 'object') return;
  if (!Object.prototype.hasOwnProperty.call(base, field) || base[field] === undefined) return;
  // Explicitly written provenance (different from the current one) wins.
  if (explicit !== undefined && explicit !== null && typeof explicit === 'object') {
    const ev = fieldOf(explicit, field);
    const cv =
      curObj !== null && curObj !== undefined && typeof curObj === 'object' ? fieldOf(curObj, field) : undefined;
    if (!valueEquals(ev, cv)) return;
  }
  if (OPTIONAL_CONTAINERS.has(container)) {
    const next: Record<string, unknown> = { ...base };
    Reflect.deleteProperty(next, field);
    out[container] = Object.keys(next).some((k) => next[k] !== undefined) ? next : undefined;
  } else {
    if (base[field] === null) return;
    out[container] = { ...base, [field]: null };
  }
}

/** `Ops.withShadow`: the patch expanded with the provenance clears its modelled fields imply. */
export function withShadow<T extends TableName>(
  tbl: T,
  current: TableRecords[T],
  patch: RecordPatch<TableRecords[T]>,
): RecordPatch<TableRecords[T]> {
  return applyShadowRules(tbl, current, patch) as RecordPatch<TableRecords[T]>;
}

/** Shadow rules of the `pkg` singleton (`rpeVersion` drops the verbatim RPEVersion lexeme). */
export function withShadowPkg(
  current: PackageSettings,
  patch: RecordPatch<PackageSettings>,
): RecordPatch<PackageSettings> {
  return applyShadowRules('pkg', current, patch);
}
